import React, { useEffect, useState } from 'react';
import { X, Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CartSheet = ({ isOpen, onClose }) => {
  const { cart, updateQuantity, removeFromCart, cartTotal, cartCount } = useCart();
  const [enrichedCart, setEnrichedCart] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    // Always mirror the source cart into local state — even when it goes to 0
    // items. The previous version only ran on `cart.length > 0`, so deleting
    // the last (or only) item left the removed row visible in the drawer and
    // the "delete doesn't work" bug the user reported.
    if (!isOpen) return;
    if (cart.length === 0) { setEnrichedCart([]); return; }
    const load = async () => {
      const map = {};
      for (const item of cart) {
        if (!map[item.product_id]) {
          try {
            const { data } = await axios.get(`${API_URL}/api/products/${item.product_id}`);
            map[item.product_id] = data;
          } catch {}
        }
      }
      setEnrichedCart(cart.map(i => ({ ...i, product: map[i.product_id] || i.product })));
    };
    load();
  }, [cart, isOpen]);

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/30 backdrop-blur-[2px] z-[599]" onClick={onClose} data-testid="cart-overlay" />}
      <div className={`cart-sheet-luxe ${isOpen ? 'open' : ''}`} data-testid="cart-sheet">
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-5 sm:px-7 py-5 border-b border-[#E5E0D8]">
            <div className="flex items-baseline gap-2">
              <span className="overline">Shopping Bag</span>
              <span className="text-[11px] text-[#595956]">{cartCount} {cartCount === 1 ? 'item' : 'items'}</span>
            </div>
            <button onClick={onClose} className="w-8 h-8 -mr-1 flex items-center justify-center text-[#595956] hover:text-[#1B1815] transition-colors" data-testid="close-cart-button">
              <X size={20} strokeWidth={1} />
            </button>
          </div>

          {enrichedCart.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-5 px-8 text-center">
              <ShoppingBag size={34} strokeWidth={1} className="text-[#CBB894]" />
              <div>
                <p className="text-lg" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: 'italic' }}>Your bag is empty</p>
                <p className="text-[12px] text-[#595956] mt-1.5">Discover something you'll love.</p>
              </div>
              <button onClick={() => { onClose(); navigate('/products'); }} className="btn-luxe-outline text-[10px] py-2.5 px-6" data-testid="cart-continue-shopping">
                Continue Shopping
              </button>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5 divide-y divide-[#EFE9DE]">
                {enrichedCart.map((item, i) => {
                  const price = item.product?.price || 0;
                  return (
                    <div key={`${item.product_id}-${item.size}-${item.color}`} className="flex gap-4 py-5 first:pt-0" data-testid={`cart-item-${i}`}>
                      <img src={item.product?.images?.[0]} alt={item.product?.name} loading="lazy" decoding="async" className="w-[76px] h-[100px] object-cover bg-[#F6F3EE] rounded-sm shrink-0" />
                      <div className="flex-1 min-w-0 flex flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-[13px] font-medium leading-snug line-clamp-2 pr-1">{item.product?.name}</p>
                          <button onClick={() => removeFromCart(item.product_id, item.size, item.color)} className="text-[#B0AAA0] hover:text-[#8A4B4B] transition-colors shrink-0 -mt-0.5" data-testid={`remove-item-${i}`} aria-label="Remove item">
                            <Trash2 size={14} strokeWidth={1.5} />
                          </button>
                        </div>
                        {(item.size || item.color) && (
                          <p className="text-[11px] text-[#595956] mt-1">
                            {[item.size, item.color].filter(Boolean).join(' · ')}
                          </p>
                        )}
                        <div className="flex items-end justify-between gap-2 mt-auto pt-3">
                          <div className="flex items-center border border-[#E5E0D8] rounded-full">
                            <button onClick={() => updateQuantity(item.product_id, item.size, item.color, Math.max(1, item.quantity - 1))} className="w-8 h-8 flex items-center justify-center text-[#595956] hover:text-[#B08D57] transition-colors disabled:opacity-30" disabled={item.quantity <= 1} data-testid={`decrease-qty-${i}`}>
                              <Minus size={12} strokeWidth={1.5} />
                            </button>
                            <span className="text-xs w-6 text-center tabular-nums" data-testid={`qty-${i}`}>{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.product_id, item.size, item.color, item.quantity + 1)} className="w-8 h-8 flex items-center justify-center text-[#595956] hover:text-[#B08D57] transition-colors" data-testid={`increase-qty-${i}`}>
                              <Plus size={12} strokeWidth={1.5} />
                            </button>
                          </div>
                          <span className="text-[13px]" style={{ fontFamily: "'Manrope', sans-serif" }}>₹{(price * item.quantity).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="px-5 sm:px-7 py-5 border-t border-[#E5E0D8] bg-[#FCFBF9]">
                {(() => {
                  const itemsWithGst = enrichedCart.map(i => ({ ...i, price: i.product?.price || 0, quantity: i.quantity, gst_rate: i.product?.gst_rate ?? 18 }));
                  const t = (() => {
                    let base = 0, tax = 0;
                    itemsWithGst.forEach(i => {
                      const lm = i.price * i.quantity;
                      const b = lm / (1 + i.gst_rate / 100);
                      base += b; tax += (lm - b);
                    });
                    return { base: base.toFixed(2), tax: tax.toFixed(2) };
                  })();
                  return (
                    <div className="space-y-2 text-[12px] mb-4 pb-4 border-b border-[#EFE9DE]">
                      <div className="flex justify-between"><span className="text-[#595956]">Sub-total</span><span>₹{t.base}</span></div>
                      <div className="flex justify-between"><span className="text-[#595956]">GST (incl.)</span><span>₹{t.tax}</span></div>
                      <div className="flex justify-between"><span className="text-[#595956]">Shipping</span><span className="text-[#B08D57]">Complimentary</span></div>
                    </div>
                  );
                })()}
                <div className="flex items-center justify-between mb-5">
                  <span className="overline">Total</span>
                  <span className="text-xl" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="cart-total">₹{cartTotal.toFixed(2)}</span>
                </div>
                <button onClick={() => { onClose(); navigate('/checkout'); }} className="w-full btn-luxe" data-testid="checkout-button">
                  Proceed to Checkout
                </button>
                <p className="text-center text-[10px] text-[#595956] mt-3 tracking-wider uppercase">MRP includes 18% GST</p>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CartSheet;
